import { useRef, useState } from "react";
import classNames from "classnames";
import style from "./data-editor-integration-item.module.css";
import { MondayLogo } from "frontend/icons/monday-logo";
import ChevronIcon from "frontend/ui-components/design-system/icons/chevron";
import ThreeDotsIcon from "frontend/ui-components/design-system/icons/three-dots-icon";
import { useHover, useOnClickOutside } from "usehooks-ts";
import { PortalID } from "frontend/modal/portal-types";
import DeleteIcon from "frontend/ui-components/design-system/icons/delete";
import { Floater } from "frontend/ui-components/floaters/floater";
import ReactDOM from "react-dom";
import Checkbox from "frontend/ui-components/design-system/checkbox/checkbox";
import { useFieldDisplay } from "frontend/canvas-designer-new/element-dynamic-data/hooks/use-field-display";
import { useDynamicIntegrations } from "frontend/canvas-designer-new/element-dynamic-data/hooks/use-dynamic-integrations";
import { Tooltip } from "frontend/ui-components/floaters/tooltip";
import { MissingColumnInFields, MissingColumnsInItem } from "./missing-columns-indicator";
import { CanvasElement } from "shared/datamodel/schemas";
import { MissingDataIcon } from "frontend/canvas-designer-new/element-dynamic-data/icons/missing-data-icon";
import { UNSUPPORTED_COLUMNS } from "frontend/canvas-designer-new/element-dynamic-data/consts";

interface DataEditorIntegrationItemProps {
  elementId: string;
  element: CanvasElement;
  integrationId: string;
  itemId: string;
  columns: { id: string; type?: string; title?: string }[];
  startExpanded?: boolean;
  isMissing?: boolean;
}

function DataEditorIntegrationItem({
  elementId,
  element,
  integrationId,
  itemId,
  columns,
  startExpanded = false,
  isMissing = false,
}: DataEditorIntegrationItemProps) {
  const {
    isLoading,
    getFieldValue,
    removeIntegrationItem,
    getIntegrationItemConfiguration,
    integrationsConfig,
    getDisplayedColumnIds,
  } = useDynamicIntegrations(elementId, element);

  const [expanded, setExpanded] = useState(startExpanded);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const itemName = getFieldValue("integration", integrationId, itemId, "name");
  const itemConfig = getIntegrationItemConfiguration(integrationId, itemId);

  const getSupportedColumns = () => {
    return columns.filter((col) => col.type && !UNSUPPORTED_COLUMNS.has(col.type));
  };

  const getFilteredColumns = (allColumns: { id: string; type?: string; title?: string }[]) => {
    if (!itemConfig) return [];
    if (itemConfig.columnIds === "all") {
      return allColumns;
    }
    if (itemConfig.columnIds.length !== undefined) {
      return allColumns.filter((col) => itemConfig.columnIds.includes(col.id));
    }
    return allColumns.filter((col) =>
      integrationsConfig[integrationId]?.columns?.find((c: { id: string }) => c.id === col.id)
    );
  };

  const supportedColumns = getSupportedColumns();
  const filteredColumns = getFilteredColumns(supportedColumns);
  const displayedColumnIds = getDisplayedColumnIds();

  const missingColumns =
    isLoading || isMissing
      ? 0
      : displayedColumnIds.filter((id) => !filteredColumns.some((col) => col.id === id)).length;

  function renderRow() {
    const icon = isMissing ? (
      <MissingDataIcon className={style.mondayLogo} />
    ) : (
      <MondayLogo className={style.mondayLogo} size={16} />
    );
    const name = isMissing ? "Missing item" : itemName;
    const allowExpand = !isMissing;

    return (
      <div
        className={classNames(style.row, { [style.missing]: isMissing })}
        onClick={() => {
          if (allowExpand) setExpanded(!expanded);
        }}
      >
        {icon}
        <div className={style.nameAndArrow}>
          <span className={classNames(style.itemName, { [style.missing]: isMissing })}>{name}</span>
          {allowExpand && <ChevronIcon className={classNames(style.arrow, { [style.expanded]: expanded })} />}
        </div>
        {missingColumns > 0 && (
          <div className={style.missingColumnsInRow}>
            <MissingColumnsInItem missingColumns={missingColumns} />
          </div>
        )}
        <div
          ref={menuRef}
          className={style.menuButton}
          onClick={(e) => {
            e.stopPropagation();
            setShowMenu(!showMenu);
          }}
        >
          <ThreeDotsIcon width={14} height="100%" />
        </div>
      </div>
    );
  }

  function renderFields() {
    if (isMissing)
      return (
        <div className={style.fieldsContainer}>
          <div className={style.separator} />
        </div>
      );

    return (
      <div className={classNames(style.fieldsContainer, { [style.expanded]: expanded })}>
        <div ref={wrapperRef} className={style.fieldsWrapper}>
          <div className={style.fieldsContent}>
            <MissingColumnInFields missingColumns={missingColumns} />
            {filteredColumns.map(({ id, title }) => {
              const fieldId = `integration:${integrationId}:${itemId}:${id}`;
              const fieldValue = getFieldValue("integration", integrationId, itemId, id);
              return (
                <IntegrationItemField
                  key={fieldId}
                  fieldId={fieldId}
                  fieldValue={fieldValue}
                  disabled={!fieldValue}
                  columnName={title ?? "Untitled Column"}
                  elementId={elementId}
                />
              );
            })}
          </div>
        </div>
        <div className={style.separator} />
      </div>
    );
  }

  function renderMenu() {
    if (!showMenu) return null;

    return (
      <IntegrationItemSubmenu
        relativeTo={menuRef}
        closeMenu={() => setShowMenu(false)}
        onDelete={() => {
          removeIntegrationItem(integrationId, itemId);
          setShowMenu(false);
        }}
      />
    );
  }

  return (
    <>
      <div className={style.container}>
        {renderRow()}
        {renderFields()}
      </div>
      {renderMenu()}
    </>
  );
}

function IntegrationItemField({
  fieldId,
  columnName,
  elementId,
  fieldValue,
  disabled,
}: {
  fieldId: string;
  columnName: string;
  elementId: string;
  fieldValue?: string;
  disabled: boolean;
}) {
  const { visible, toggleField } = useFieldDisplay(elementId, fieldId, fieldValue);
  const isDisabled = disabled && !visible; // Allow unchecking empty fields, but not checking them.

  const fieldRef = useRef<HTMLDivElement>(null);
  const hover = useHover(fieldRef);

  return (
    <>
      <div className={style.field} ref={fieldRef}>
        <Checkbox checked={visible} theme="dark" onChange={toggleField} disabled={isDisabled} />
        <span
          key={fieldId}
          className={classNames(style.columnName, { [style.disabled]: isDisabled })}
          onClick={() => {
            if (isDisabled) return;
            toggleField();
          }}
        >
          {columnName}
        </span>
      </div>
      {hover && isDisabled && (
        <Tooltip
          label="Column is empty"
          relativeTo={fieldRef}
          side="left"
          customStyle={{ borderRadius: "6px", fontSize: "10px", backgroundColor: "#355270" }}
          margin={10}
          arrowSize={8}
        />
      )}
    </>
  );
}

function IntegrationItemSubmenu({
  relativeTo,
  closeMenu,
  onDelete,
}: {
  relativeTo: React.RefObject<HTMLElement>;
  closeMenu: () => void;
  onDelete: () => void;
}) {
  const ref = useRef(null);
  useOnClickOutside(ref, closeMenu, "mouseup");
  const portal = document.getElementById(PortalID.Floaters);

  const element = relativeTo.current ? (
    <Floater ref={ref} relativeTo={relativeTo} side={"bottom"} boundary={"none"} margin={-5}>
      <div className={style.menu}>
        <div
          className={style.deleteButton}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <DeleteIcon width={18} height={18} />
          <span>Delete</span>
        </div>
      </div>
    </Floater>
  ) : null;

  return portal ? ReactDOM.createPortal(element, portal) : element;
}

export default DataEditorIntegrationItem;
